// voiceover-cache.ts
// IndexedDB-based cache for voiceover recordings to prevent loss on upload failure

const DB_NAME = "ladder-voiceover-cache";
const DB_VERSION = 2; // Bumped for movementId -> componentId rename
const STORE_NAME = "voiceovers";

const MAX_RECORDINGS_PER_COMPONENT = 5;
const EXPIRY_DAYS = 7;

export interface CachedVoiceover {
  id: string;
  blob: Blob;
  createdAt: number;
  componentId: string; // Unique ID of movement or section
  workoutId: string;
  userId: string;
  componentName?: string;
  durationSeconds?: number;
}

export type CachedVoiceoverMeta = Omit<CachedVoiceover, "blob">;

function generateId(): string {
  return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
}

function openDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);

    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;
      const oldVersion = event.oldVersion;

      // Migration from v1 (movementId) to v2 (componentId)
      // Delete old store and recreate - cache data loss is acceptable
      if (oldVersion === 1 && db.objectStoreNames.contains(STORE_NAME)) {
        db.deleteObjectStore(STORE_NAME);
      }

      if (!db.objectStoreNames.contains(STORE_NAME)) {
        const store = db.createObjectStore(STORE_NAME, { keyPath: "id" });
        store.createIndex("componentWorkout", ["componentId", "workoutId"], {
          unique: false,
        });
        store.createIndex("createdAt", "createdAt", { unique: false });
      }
    };
  });
}

export async function saveVoiceover(
  data: Omit<CachedVoiceover, "id" | "createdAt">
): Promise<CachedVoiceover> {
  const db = await openDB();
  const entry: CachedVoiceover = {
    ...data,
    id: generateId(),
    createdAt: Date.now(),
  };

  return new Promise((resolve, reject) => {
    const transaction = db.transaction(STORE_NAME, "readwrite");
    const store = transaction.objectStore(STORE_NAME);

    const request = store.add(entry);
    request.onerror = () => reject(request.error);
    request.onsuccess = () => {
      // After saving, prune old entries for this component/workout
      pruneOldEntries(data.componentId, data.workoutId)
        .then(() => resolve(entry))
        .catch((err) => {
          console.warn("Failed to prune old voiceover entries:", err);
          resolve(entry); // Still resolve - pruning failure shouldn't block
        });
    };
  });
}

export async function getVoiceoversForComponent(
  componentId: string,
  workoutId: string
): Promise<CachedVoiceover[]> {
  const db = await openDB();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction(STORE_NAME, "readonly");
    const store = transaction.objectStore(STORE_NAME);
    const index = store.index("componentWorkout");

    const request = index.getAll([componentId, workoutId]);
    request.onerror = () => reject(request.error);
    request.onsuccess = () => {
      const results = request.result as CachedVoiceover[];
      // Sort by createdAt descending (newest first)
      results.sort((a, b) => b.createdAt - a.createdAt);
      resolve(results);
    };
  });
}

export async function getVoiceoverById(
  id: string
): Promise<CachedVoiceover | null> {
  const db = await openDB();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction(STORE_NAME, "readonly");
    const store = transaction.objectStore(STORE_NAME);

    const request = store.get(id);
    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result || null);
  });
}

export async function deleteVoiceover(id: string): Promise<void> {
  const db = await openDB();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction(STORE_NAME, "readwrite");
    const store = transaction.objectStore(STORE_NAME);

    const request = store.delete(id);
    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve();
  });
}

export async function clearAllForComponent(
  componentId: string,
  workoutId: string
): Promise<void> {
  const entries = await getVoiceoversForComponent(componentId, workoutId);
  const db = await openDB();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction(STORE_NAME, "readwrite");
    const store = transaction.objectStore(STORE_NAME);

    let completed = 0;
    const total = entries.length;

    if (total === 0) {
      resolve();
      return;
    }

    entries.forEach((entry) => {
      const request = store.delete(entry.id);
      request.onsuccess = () => {
        completed++;
        if (completed === total) resolve();
      };
      request.onerror = () => reject(request.error);
    });
  });
}

async function pruneOldEntries(
  componentId: string,
  workoutId: string
): Promise<void> {
  const entries = await getVoiceoversForComponent(componentId, workoutId);

  // Keep only the newest MAX_RECORDINGS_PER_COMPONENT
  if (entries.length > MAX_RECORDINGS_PER_COMPONENT) {
    const toDelete = entries.slice(MAX_RECORDINGS_PER_COMPONENT);
    for (const entry of toDelete) {
      await deleteVoiceover(entry.id);
    }
  }
}

export async function clearExpired(): Promise<number> {
  const db = await openDB();
  const expiryTime = Date.now() - EXPIRY_DAYS * 24 * 60 * 60 * 1000;

  return new Promise((resolve, reject) => {
    const transaction = db.transaction(STORE_NAME, "readwrite");
    const store = transaction.objectStore(STORE_NAME);
    const index = store.index("createdAt");

    // Get all entries older than expiry time
    const range = IDBKeyRange.upperBound(expiryTime);
    const request = index.openCursor(range);

    let deletedCount = 0;

    request.onerror = () => reject(request.error);
    request.onsuccess = (event) => {
      const cursor = (event.target as IDBRequest<IDBCursorWithValue>).result;
      if (cursor) {
        cursor.delete();
        deletedCount++;
        cursor.continue();
      } else {
        resolve(deletedCount);
      }
    };
  });
}

export async function getCacheStats(): Promise<{
  totalEntries: number;
  totalSizeBytes: number;
}> {
  const db = await openDB();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction(STORE_NAME, "readonly");
    const store = transaction.objectStore(STORE_NAME);

    const request = store.getAll();
    request.onerror = () => reject(request.error);
    request.onsuccess = () => {
      const entries = request.result as CachedVoiceover[];
      const totalSizeBytes = entries.reduce(
        (sum, entry) => sum + (entry.blob?.size || 0),
        0
      );
      resolve({
        totalEntries: entries.length,
        totalSizeBytes,
      });
    };
  });
}

// Check if IndexedDB is available
export function isVoiceoverCacheAvailable(): boolean {
  try {
    return typeof indexedDB !== "undefined" && indexedDB !== null;
  } catch {
    return false;
  }
}

